import React, { FC, useId } from 'react';
import { Col, Row, UncontrolledTooltip } from 'reactstrap';
import { TEXT_COLOR } from 'views/Widgets/Charts/chartColors';

interface Props {
  data: Array<{ value: number; color: string; label?: string }>;
}

// Below this width a label can't fit without visibly overflowing/clipping,
// so that segment renders as a bare color swatch instead - still
// identifiable via a legend elsewhere, or on hover (see the tooltip below),
// just without an inline label.
const MIN_LABELED_WIDTH = 0.08;

const HorizontalBar: FC<Props> = (props) => {
  // A page can render several HorizontalBars at once (e.g. one per
  // statement in a multi-statement LIKERT chart) - useId keeps each
  // instance's segment target ids unique so UncontrolledTooltip below
  // doesn't cross-wire to another bar's segment.
  const instanceId = useId();

  return (
    <Row className="gx-0" style={{ height: '1.5rem' }}>
      {props.data?.map((d, i) => {
        const isLabeled = !!d.label && d.value >= MIN_LABELED_WIDTH;
        const segmentId = `hbar-segment-${instanceId}-${i}`.replace(/:/g, '');
        return (
          <Col
            key={i}
            id={segmentId}
            className="col-auto d-flex align-items-center justify-content-center overflow-hidden"
            style={{ width: `${100 * d.value}%`, backgroundColor: d.color }}
          >
            {isLabeled && (
              <span
                className="small text-truncate"
                style={{ color: TEXT_COLOR, lineHeight: 1 }}
              >
                {d.label}
              </span>
            )}
            {/* Segment too narrow for its label - reveal the value on
                hover instead of showing nothing at all. */}
            {d.label && !isLabeled && (
              <UncontrolledTooltip target={segmentId}>
                {d.label}
              </UncontrolledTooltip>
            )}
          </Col>
        );
      })}
    </Row>
  );
};

export default React.memo(HorizontalBar);
